/**
 * components/shared/BrokerSelector/BrokerSearch.tsx
 * Reusable broker search input component
 * 
 * Provides real-time search functionality for broker selection.
 * Used across onboarding, settings, and account management.
 */

// 1. External dependencies
import React from 'react';
import { Search } from 'lucide-react';

// 2. Internal absolute imports
import { Input } from '@enfomentis/ui';

/**
 * Props for BrokerSearch component
 */
export interface BrokerSearchProps {
  /** Current search query */
  value: string;
  
  /** Search query change handler */
  onChange: (query: string) => void;
  
  /** Placeholder text for the search input */
  placeholder?: string;
  
  /** Whether the search input is disabled */
  disabled?: boolean;
  
  /** Optional additional CSS classes */
  className?: string;
  
  /** Whether to show search results count */
  showResultsCount?: boolean;
  
  /** Number of results found (if showing count) */
  resultsCount?: number;
  
  /** Total number of available items (if showing count) */
  totalCount?: number;
}

/**
 * Reusable broker search component
 */
export const BrokerSearch: React.FC<BrokerSearchProps> = ({
  value,
  onChange,
  placeholder = 'Search brokers...',
  disabled = false,
  className = '',
  showResultsCount = false,
  resultsCount = 0,
  totalCount = 0
}) => {

  return (
    <div className={className}>
      {/* Clean Search Input - No FormField wrapper, no helper text */}
      <Input
        type="text"
        value={value}
        onValueChange={onChange}
        placeholder={placeholder}
        isDisabled={disabled}
        startContent={<Search className="text-default-400" size={16} />}
        isClearable
      />
      
      {/* Results Count - Now hidden by default, can be removed entirely later */}
      {showResultsCount && (
        <div className="text-sm text-text-secondary mt-2">
          <span>
            {value ? (
              resultsCount === 0 ? (
                'No brokers found'
              ) : resultsCount === 1 ? (
                '1 broker found'
              ) : (
                `${resultsCount} brokers found`
              )
            ) : (
              totalCount === 1 ? (
                '1 broker available'
              ) : (
                `${totalCount} brokers available`
              )
            )}
          </span>
        </div>
      )}
    </div>
  );
};